/* eslint-disable max-lines */
import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { CURRENT_WINDOW_DAYS, getStateDisplayName } from '../../../store/utils/shipmentUtils';
import { selectAccessibleFacilities } from '../../../store/selectors/shipmentModuleSelectors';
import {
  navigateToAwaitingFacility,
  navigateToCategory,
  navigateToShippedSubcategory,
} from '../../../store/thunks/shipstationShipmentThunks';

const CountBadge = ({ count, loading }) => (
  <Chip
    label={loading ? '...' : count}
    size='small'
    sx={{
      height: 20,
      fontSize: '0.75rem',
      minWidth: 40,
      '& .MuiChip-label': { px: 1.5 },
    }}
  />
);

CountBadge.propTypes = {
  count: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
};

const ShipstationCategoryMenu = () => {
  const dispatch = useDispatch();
  const { activeCategory, activeSubcategory, activeFacility, counts } = useSelector(
    ({ shipstationShipment }) => shipstationShipment
  );
  const facilities = useSelector(selectAccessibleFacilities);

  const [awaitingOpen, setAwaitingOpen] = React.useState(true);
  const [shippedOpen, setShippedOpen] = React.useState(true);

  React.useEffect(() => {
    if (activeCategory === 'awaitingShipment') setAwaitingOpen(true);
    if (activeCategory === 'shipped') setShippedOpen(true);
  }, [activeCategory]);

  const isAwaitingFacilitySelected = (pharmacyId) =>
    activeCategory === 'awaitingShipment' && activeFacility === pharmacyId;

  const isShippedSubcategorySelected = (subcategory) =>
    activeCategory === 'shipped' && activeSubcategory === subcategory;

  return (
    <Box sx={{ overflow: 'auto', height: '100%' }}>
      <List dense disablePadding sx={{ pt: 0.5 }}>
        {/* AWAITING SHIPMENT */}
        <ListItemButton
          onClick={() => setAwaitingOpen(!awaitingOpen)}
          sx={{ py: 0.5, px: 2, minHeight: 32 }}
        >
          <ListItemText
            primary={
              <Typography
                variant='subtitle2'
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.7rem',
                  letterSpacing: 0.5,
                }}
              >
                Awaiting Shipment
              </Typography>
            }
            sx={{ flex: '1 1 auto', minWidth: 0 }}
          />
          <Box
            component='span'
            sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <CountBadge count={counts.awaiting.total} loading={counts.loading} />
            {awaitingOpen ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
          </Box>
        </ListItemButton>

        <Collapse in={awaitingOpen} timeout='auto' unmountOnExit>
          <List dense disablePadding>
            {/* All */}
            <ListItemButton
              selected={isAwaitingFacilitySelected('all')}
              onClick={() => dispatch(navigateToCategory({ category: 'awaitingShipment' }))}
              sx={{ py: 0.25, pl: 3, pr: 2, minHeight: 28 }}
            >
              <ListItemText
                primary='All'
                primaryTypographyProps={{ fontSize: '0.85rem' }}
                sx={{ flex: '1 1 auto', minWidth: 0 }}
              />
              <Box component='span' sx={{ flexShrink: 0, ml: 0.5 }}>
                <CountBadge count={counts.awaiting.total} loading={counts.loading} />
              </Box>
            </ListItemButton>

            {/* Facilities */}
            {facilities.map((facility) => {
              const pharmacyCount = counts.awaiting.byPharmacy[facility.fulfillingPharmacyId] || 0;
              return (
                <ListItemButton
                  key={facility.fulfillingPharmacyId}
                  selected={isAwaitingFacilitySelected(facility.fulfillingPharmacyId)}
                  onClick={() =>
                    dispatch(
                      navigateToAwaitingFacility({ pharmacyId: facility.fulfillingPharmacyId })
                    )
                  }
                  sx={{ py: 0.25, pl: 3, pr: 2, minHeight: 28 }}
                >
                  <ListItemText
                    primary={
                      facility.state
                        ? `${facility.name} (${getStateDisplayName(facility.state)})`
                        : facility.name
                    }
                    primaryTypographyProps={{ fontSize: '0.85rem', noWrap: true }}
                    sx={{ flex: '1 1 auto', minWidth: 0 }}
                  />
                  <Box component='span' sx={{ flexShrink: 0, ml: 0.5 }}>
                    <CountBadge count={pharmacyCount} loading={counts.loading} />
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        </Collapse>

        <Divider sx={{ my: 0.5 }} />

        {/* SHIPPED */}
        <ListItemButton
          onClick={() => setShippedOpen(!shippedOpen)}
          sx={{ py: 0.5, px: 2, minHeight: 32 }}
        >
          <ListItemText
            primary={
              <Typography
                variant='subtitle2'
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.7rem',
                  letterSpacing: 0.5,
                }}
              >
                Shipped
              </Typography>
            }
            sx={{ flex: '1 1 auto', minWidth: 0 }}
          />
          <Box
            component='span'
            sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            {shippedOpen ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
          </Box>
        </ListItemButton>

        <Collapse in={shippedOpen} timeout='auto' unmountOnExit>
          <List dense disablePadding>
            {/* Today */}
            <ListItemButton
              selected={isShippedSubcategorySelected('today')}
              onClick={() => dispatch(navigateToShippedSubcategory({ subcategory: 'today' }))}
              sx={{ py: 0.25, pl: 3, pr: 2, minHeight: 28 }}
            >
              <ListItemText
                primary='Today'
                primaryTypographyProps={{ fontSize: '0.85rem' }}
                sx={{ flex: '1 1 auto', minWidth: 0 }}
              />
              <Box component='span' sx={{ flexShrink: 0, ml: 0.5 }}>
                <CountBadge count={counts.shipped.today} loading={counts.loading} />
              </Box>
            </ListItemButton>

            {/* Yesterday */}
            <ListItemButton
              selected={isShippedSubcategorySelected('yesterday')}
              onClick={() => dispatch(navigateToShippedSubcategory({ subcategory: 'yesterday' }))}
              sx={{ py: 0.25, pl: 3, pr: 2, minHeight: 28 }}
            >
              <ListItemText
                primary='Yesterday'
                primaryTypographyProps={{ fontSize: '0.85rem' }}
                sx={{ flex: '1 1 auto', minWidth: 0 }}
              />
              <Box component='span' sx={{ flexShrink: 0, ml: 0.5 }}>
                <CountBadge count={counts.shipped.yesterday} loading={counts.loading} />
              </Box>
            </ListItemButton>

            {/* Last Nd */}
            <ListItemButton
              selected={isShippedSubcategorySelected('recent')}
              onClick={() => dispatch(navigateToShippedSubcategory({ subcategory: 'recent' }))}
              sx={{ py: 0.25, pl: 3, pr: 2, minHeight: 28 }}
            >
              <ListItemText
                primary={`Last ${CURRENT_WINDOW_DAYS}d`}
                primaryTypographyProps={{ fontSize: '0.85rem' }}
                sx={{ flex: '1 1 auto', minWidth: 0 }}
              />
              <Box component='span' sx={{ flexShrink: 0, ml: 0.5 }}>
                <CountBadge count={counts.shipped.recent} loading={counts.loading} />
              </Box>
            </ListItemButton>

            {/* All */}
            <ListItemButton
              selected={isShippedSubcategorySelected('all')}
              onClick={() => dispatch(navigateToShippedSubcategory({ subcategory: 'all' }))}
              sx={{ py: 0.25, pl: 3, pr: 2, minHeight: 28 }}
            >
              <ListItemText
                primary='All'
                primaryTypographyProps={{ fontSize: '0.85rem' }}
                sx={{ flex: '1 1 auto', minWidth: 0 }}
              />
            </ListItemButton>

            {/* Voided */}
            <ListItemButton
              selected={isShippedSubcategorySelected('voided')}
              onClick={() => dispatch(navigateToShippedSubcategory({ subcategory: 'voided' }))}
              sx={{ py: 0.25, pl: 3, pr: 2, minHeight: 28 }}
            >
              <ListItemText
                primary='Voided'
                primaryTypographyProps={{ fontSize: '0.85rem' }}
                sx={{ flex: '1 1 auto', minWidth: 0 }}
              />
            </ListItemButton>
          </List>
        </Collapse>
      </List>
    </Box>
  );
};

export default ShipstationCategoryMenu;
